import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowUpRight, EnvelopeSimple, MapPin } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const teamSizes = ["1–10", "11–50", "51–250", "250–1,000", "1,000+"];

export default function ContactPage() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    role: "",
    team_size: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Your message landed. A senior architect will reach out within 24 hours.");
      setForm({ name: "", email: "", company: "", role: "", team_size: "", message: "" });
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        "Could not send your message. Please try again or email us directly.";
      toast.error(typeof detail === "string" ? detail : "Validation failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      {/* HERO */}
      <section className="border-b border-zinc-200">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-24 lg:py-32">
          <div className="eyebrow mb-6">Talk to engineering</div>
          <motion.h1
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.98] tracking-tight max-w-4xl"
          >
            One form. A senior architect.
            <br />
            <span className="text-blue-700">Within 24 hours.</span>
          </motion.h1>
          <p className="mt-8 text-lg text-zinc-600 max-w-2xl leading-relaxed">
            No discovery calls with junior reps. No multi-week sales relays.
            Tell us what you&apos;re building — we&apos;ll route it to the right
            engineer the same day.
          </p>
        </div>
      </section>

      {/* FORM + INFO */}
      <section className="border-b border-zinc-200">
        <div className="mx-auto max-w-[1400px] grid grid-cols-1 lg:grid-cols-12">
          {/* FORM */}
          <div className="lg:col-span-8 p-6 sm:p-10 lg:p-16 border-b lg:border-b-0 lg:border-r border-zinc-200">
            <form onSubmit={handleSubmit} className="space-y-8" data-testid="contact-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <Field
                  label="Your name"
                  testId="contact-name"
                  value={form.name}
                  onChange={update("name")}
                  required
                  placeholder="Ada Lovelace"
                />
                <Field
                  label="Work email"
                  testId="contact-email"
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  required
                  placeholder="ada@company.com"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <Field
                  label="Company"
                  testId="contact-company"
                  value={form.company}
                  onChange={update("company")}
                  required
                  placeholder="Acme Inc."
                />
                <Field
                  label="Role"
                  testId="contact-role"
                  value={form.role}
                  onChange={update("role")}
                  placeholder="VP Engineering"
                />
              </div>
              <div>
                <Label>Team size</Label>
                <div className="mt-3 flex flex-wrap gap-2" data-testid="contact-team-size">
                  {teamSizes.map((size) => (
                    <button
                      key={size}
                      type="button"
                      data-testid={`team-size-${size.replace(/[^a-z0-9]/gi, "")}`}
                      onClick={() => setForm((f) => ({ ...f, team_size: size }))}
                      className={`px-4 py-2 text-sm border transition-colors ${
                        form.team_size === size
                          ? "bg-zinc-950 text-white border-zinc-950"
                          : "bg-white text-zinc-700 border-zinc-300 hover:border-zinc-500"
                      }`}
                    >
                      {size}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label>What are you building?</Label>
                <textarea
                  data-testid="contact-message"
                  value={form.message}
                  onChange={update("message")}
                  required
                  rows={6}
                  placeholder="Briefly describe your stack, current pain, and what 'better' looks like."
                  className="mt-3 w-full bg-white border border-zinc-300 px-4 py-3 text-base text-zinc-950 placeholder:text-zinc-400 focus:outline-none focus:border-blue-700 focus:ring-2 focus:ring-blue-700/15 resize-none"
                />
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-2">
                <p className="text-xs text-zinc-500">
                  By submitting, you agree to be contacted by Infranova
                  engineering. We never share your details.
                </p>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit-button"
                  className="group inline-flex items-center gap-2 bg-blue-700 text-white px-7 py-3.5 text-sm font-medium hover:bg-blue-800 transition-colors disabled:opacity-60 disabled:cursor-not-allowed w-fit"
                >
                  {submitting ? "Sending…" : "Send to engineering"}
                  <ArrowUpRight
                    size={16}
                    weight="bold"
                    className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                  />
                </button>
              </div>
            </form>
          </div>

          {/* INFO */}
          <aside className="lg:col-span-4 p-6 sm:p-10 lg:p-12 bg-zinc-50">
            <div className="eyebrow mb-6">Other channels</div>
            <ul className="space-y-7">
              <InfoRow
                icon={EnvelopeSimple}
                label="Email engineering"
                value="engineering@infranova.sh"
              />
              <InfoRow
                icon={MapPin}
                label="Headquarters"
                value="San Francisco"
              />
            </ul>

            <div className="mt-12 pt-8 border-t border-zinc-200">
              <div className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                Status
              </div>
              <div className="mt-3 flex items-center gap-2 text-sm">
                <span className="w-2 h-2 rounded-full bg-emerald-500" />
                All systems operational
              </div>
              <div className="mt-1 font-mono-tech text-xs text-zinc-500">
                99.999% — last 90 days
              </div>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}

function Label({ children }) {
  return (
    <label className="font-mono-tech text-[11px] uppercase tracking-[0.22em] text-zinc-500 font-medium">
      {children}
    </label>
  );
}

function Field({ label, value, onChange, type = "text", required, placeholder, testId }) {
  return (
    <div>
      <Label>{label}</Label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        placeholder={placeholder}
        data-testid={testId}
        className="mt-3 w-full bg-white border border-zinc-300 px-4 py-3 text-base text-zinc-950 placeholder:text-zinc-400 focus:outline-none focus:border-blue-700 focus:ring-2 focus:ring-blue-700/15"
      />
    </div>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <li className="flex items-start gap-4">
      <span className="w-10 h-10 grid place-items-center border border-zinc-200 bg-white flex-shrink-0">
        <Icon size={18} weight="duotone" className="text-blue-700" />
      </span>
      <div>
        <div className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-zinc-500">
          {label}
        </div>
        <div className="mt-1 text-sm text-zinc-950 font-medium">{value}</div>
      </div>
    </li>
  );
}
